import { Button, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import styled from 'styled-components';
import tw from 'twin.macro';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

const DialogContainer = styled.div`
  ${tw`fixed inset-0 overflow-y-auto`}
  z-index: var(--z-modal);
  background-color: rgb(17 24 39 / 0.55);
  backdrop-filter: blur(2px);
`;

const DialogContent = styled.div`
  ${tw`flex min-h-full items-center justify-center p-4`}
`;

const StyledDialogPanel = styled(DialogPanel)`
  ${tw`w-full max-w-md p-6 duration-200 ease-out`}
  background-color: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-overlay);
  &[data-closed] {
    ${tw`opacity-0 scale-95`}
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  ${tw`text-lg font-semibold`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
  letter-spacing: -0.01em;
`;

const DialogText = styled.div`
  ${tw`mt-2 text-sm leading-6`}
  color: var(--color-text-secondary);
`;

const ButtonRow = styled.div`
  ${tw`mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2`}
`;

const baseButton = tw`inline-flex items-center justify-center rounded-lg px-4 h-10 text-sm font-semibold cursor-pointer border focus:outline-none`;

const ConfirmButton = styled(Button)`
  ${baseButton}
  background-color: var(--color-black);
  color: var(--color-on-primary);
  border-color: var(--color-black);
  box-shadow: var(--shadow-xs);
  &[data-hover] {
    background-color: var(--color-black-hover);
  }
  &[data-focus] {
    box-shadow: var(--shadow-focus);
  }
`;

const CancelButton = styled(Button)`
  ${baseButton}
  background-color: var(--color-bg);
  color: var(--color-text-primary);
  border-color: var(--color-border-strong);
  &[data-hover] {
    background-color: var(--color-bg-secondary);
  }
  &[data-focus] {
    box-shadow: var(--shadow-focus);
  }
`;

interface IBaseDialogProps {
  onClose: () => void;
  isOpen: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  renderChildren?: () => React.ReactNode;
}

export default function BaseDialog({
  title = 'Are you sure?',
  description = 'This action is irreversible',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onClose,
  isOpen,
  onConfirm,
  onCancel,
  renderChildren,
}: IBaseDialogProps) {
  return (
    <Dialog open={isOpen} as="div" onClose={onClose}>
      <DialogContainer>
        <DialogContent>
          <StyledDialogPanel transition>
            <StyledDialogTitle as="h3">{title}</StyledDialogTitle>
            <DialogText>
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>{description}</ReactMarkdown>
            </DialogText>
            {renderChildren && renderChildren()}
            <ButtonRow>
              <CancelButton onClick={onCancel ?? onClose}>{cancelText}</CancelButton>
              <ConfirmButton onClick={onConfirm ?? onClose}>{confirmText}</ConfirmButton>
            </ButtonRow>
          </StyledDialogPanel>
        </DialogContent>
      </DialogContainer>
    </Dialog>
  );
}
